import TagManager from "react-gtm-module";
import { Button } from "@sellar-io/components";
import { useMatch, useNavigate, useSearchParams } from "react-router";
import { useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import { LANDING, NEW_VENUE } from "~/constants/routes";
import { BusinessFormCreate } from "~/forms/business/BusinessFormCreate";
import { useSellarUser } from "~/hooks";
import { useBusinessClaim } from "~/hooks/api/v2/business/useBusinessClaim";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "./BuyerCreationFlowStore";
import { BuyerCreationPostcodeStep } from "./BuyerCreationPostcodeStep";
import { BuyerSignupBusinessClaimed } from "./BuyerSignupBusinessClaimed/BuyerSignupBusinessClaimed";
import { BuyerSignupBusinessMatchStep } from "./BuyerSignupBusinessMatchStep";
import { BuyerSignupFormStep } from "./BuyerSignupFormStep";
import { BuyerSignupGooglePlacesSearchStep } from "./BuyerSignupGooglePlacesSearchStep";
import { BuyerSignupClaimMismatch } from "./BuyerSignupClaimMismatch";
import { BuyerFindCompanyStep } from "./BuyerFindCompanyStep";
import { BuyerBusinessConnectStep } from "./BuyerBusinessConnectStep";

export const BuyerCreationStepContent = ({
  lockEmail = false,
}: {
  lockEmail?: boolean;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderFlowToken = searchParams.get("orderFlowToken");
  const { step, searchGooglePlacesResult, reset, setStep } =
    useBuyerCreationFlowStore(
      useShallow((state) => ({
        step: state.step,
        searchGooglePlacesResult: state.searchGooglePlacesResult,
        reset: state.reset,
        setStep: state.setStep,
      })),
    );
  const { business } = useSellarCurrentBusiness();
  const { changeBusiness } = useSellarUser();
  const isNewVenue = !!useMatch(NEW_VENUE); // this route shows when creating business via dashboard, not sign up flow
  const claimBusinessMutation = useBusinessClaim();

  // Auto-skip to business connect step for orderFlowToken flow
  useEffect(() => {
    if (
      orderFlowToken &&
      step?.name === BuyerCreationFlowStep.BuyerFindCompanyStep.name
    ) {
      setStep(BuyerCreationFlowStep.BuyerBusinessConnectStep);
    }
  }, [orderFlowToken, step, setStep]);

  const renderStep = () => {
    switch (step?.name) {
      // search for limited company - skipped if buyer created via dashboard
      case BuyerCreationFlowStep.BuyerFindCompanyStep.name:
        return <BuyerFindCompanyStep isNewVenue={isNewVenue} />;

      // enter postcode of venue associated with
      case BuyerCreationFlowStep.BuyerPostcodeStep.name:
        return <BuyerCreationPostcodeStep />;

      // pick the buyer on the platform that you claim to be associated with, if same domain, match
      case BuyerCreationFlowStep.BuyerBusinessMatchStep.name:
        return <BuyerSignupBusinessMatchStep />;

      // could not find buyer on Sellar, find using google places to prefill
      case BuyerCreationFlowStep.BuyerBusinessSearchStep.name:
        return <BuyerSignupGooglePlacesSearchStep />;

      case BuyerCreationFlowStep.BuyerBusinessFormStep.name:
        return (
          <>
            {isNewVenue && business ? (
              // form for creating another venue/supplier in dashboard, not sign up flow
              <>
                <BusinessFormCreate
                  mode="business"
                  type={"buyer"}
                  formId="create-venue-form"
                  searchGooglePlacesResult={searchGooglePlacesResult}
                  onCreate={(business) => {
                    // after creating via switch business account, claim it and switch to it
                    claimBusinessMutation.mutate(
                      {
                        data: {
                          id: business.id,
                          hasAcceptedTermsAndConditions: false,
                        },
                      },
                      {
                        onSuccess: () => {
                          TagManager.dataLayer({
                            dataLayer: {
                              event: "venue_claimed",
                              businessId: business.id,
                            },
                          });

                          changeBusiness(business.id);
                          reset();
                          navigate(LANDING);
                        },
                      },
                    );
                  }}
                  metadata={{
                    initiator: business.type,
                    createdFrom: "Business switcher",
                    source: "new-venue",
                  }}
                  lockEmail={lockEmail}
                />
                <div className="mt-4">
                  <Button type="submit" form="create-venue-form">
                    Save
                  </Button>
                </div>
              </>
            ) : (
              // form for creating buyer during sign up flow (direct and dialog), not dashboard
              <BuyerSignupFormStep />
            )}
          </>
        );

      // claims venue if no business users associated, otherwise displays error
      case BuyerCreationFlowStep.BuyerBusinessClaimed.name:
        return <BuyerSignupBusinessClaimed />;

      // shows if user could not join the buyer they attempt to match with
      case BuyerCreationFlowStep.BuyerClaimMismatch.name:
        return <BuyerSignupClaimMismatch />;

      // auto-connects buyer for orderFlowToken flow
      case BuyerCreationFlowStep.BuyerBusinessConnectStep.name:
        return <BuyerBusinessConnectStep />;
    }
  };
  return <>{renderStep()}</>;
};
